import useDisabledAccount from '../hooks/account/UseDisabledAccount';
import { useAccount } from '../contexts/account/AccountContexts';
import { Navigate, useLocation } from 'react-router-dom';
import { useAccountSubscriptionSocket } from '../hooks/socket/UseAccountSubscriptionSocket';
import { FAQ, LOGIN, PRICING, QUEST_FAQ, SETTINGS, TRIAL_ENDED } from './path';
import Layout from '../components/common/layout/Layout';
import TrialEndedPage from '../../pages/trial-ended/TrialEndedPage';
import Wrapper from '../components/common/wrapper/Wrapper';
import PricingPage from '../../pages/pricing/PricingPage';
import { useAuth } from '../contexts/auth/AuthContext';
import { SocketProvider } from '../contexts/socket/SocketContext';
import { ensureAttributionCookieFromRoute } from '../services/account-attribution/AccountAttributionService';

const PrivateRoutesWithSocket = ({ children }: React.PropsWithChildren<{}>) => {
  const { isDisabled } = useDisabledAccount();
  const { accountResponse } = useAccount();
  const location = useLocation();

  useAccountSubscriptionSocket();

  const isSettingsAddress = location.pathname.startsWith(SETTINGS);
  const isPricing = location.pathname.startsWith(PRICING);
  const isFAQ = location.pathname.startsWith(FAQ);
  const isTrialEnded = location.pathname.startsWith(TRIAL_ENDED);

  if (isDisabled && !isSettingsAddress && !isPricing && !isFAQ && !isTrialEnded) {
    if (accountResponse?.accountType === 'TRIAL') {
      window.history.replaceState(null, '', TRIAL_ENDED);
      return (
        <Wrapper canShowPricing={true}>
          <Layout dataPage="trial-ended">
            <TrialEndedPage />
          </Layout>
        </Wrapper>
      );
    } else {
      window.history.replaceState(null, '', PRICING);
      return (
        <Wrapper canShowPricing={true}>
          <Layout>
            <PricingPage />
          </Layout>
        </Wrapper>
      );
    }
  }

  return <>{children}</>;
};

const PrivateRoutes = ({ children }: React.PropsWithChildren<{}>) => {
  const { isAuthenticated } = useAuth();
  const { accountResponse } = useAccount();
  const location = useLocation();

  if (!isAuthenticated) {
    ensureAttributionCookieFromRoute({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });

    if (location.pathname === FAQ) {
      return <Navigate to={QUEST_FAQ} />;
    }

    return <Navigate to={LOGIN} state={{ from: location }} replace />;
  }

  return (
    <SocketProvider accountId={accountResponse?.accountId ?? null}>
      <PrivateRoutesWithSocket>{children}</PrivateRoutesWithSocket>
    </SocketProvider>
  );
};

export default PrivateRoutes;
